import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  LayoutDashboard,
  Loader2,
  Lock,
  Pencil,
  Search,
  MoreHorizontal,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronRight,
  CornerDownRight,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { getIcon } from "@/lib/iconMap";
import {
  useModuleList,
  useReorderModules,
  useUpdateModule,
} from "../hooks/useModules";
import { IconPickerModal } from "../components/IconPickerModal";
import type { ModuleConfig, UpdateModulePayload } from "../types";

const CARD_BORDER = "hsl(210,30%,85%)";
const ADMINISTRATION_NAME = "administration";

type FieldPatch = {
  label?: string;
  icon?: string;
  isEnabled?: boolean;
  showInSidebar?: boolean;
};

function cloneModules(list: ModuleConfig[]): ModuleConfig[] {
  return list.map((m) => ({
    ...m,
    children: m.children.map((c) => ({ ...c, children: [] })),
  }));
}

function Toggle({
  checked,
  disabled,
  onChange,
  title,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
  title?: string;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={cn(
        "relative inline-flex h-[18px] w-[32px] items-center rounded-full transition-colors",
        checked ? "bg-[hsl(210,100%,50%)]" : "bg-[hsl(210,16%,80%)]",
        disabled && "opacity-50",
        !disabled && "cursor-pointer",
      )}
    >
      <span
        className={cn(
          "inline-block h-[14px] w-[14px] transform rounded-full bg-white shadow transition-transform",
          checked ? "translate-x-[16px]" : "translate-x-[2px]",
        )}
      />
    </button>
  );
}

function SystemBadge() {
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
      style={{ background: "hsl(210,55%,92%)", color: "hsl(210,100%,28%)" }}
    >
      System
    </span>
  );
}

function ParentBadge() {
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
      style={{ background: "hsl(210,16%,92%)", color: "hsl(215,16%,40%)" }}
    >
      Parent
    </span>
  );
}

export default function ModuleManagerPage() {
  const { data: modules = [], isLoading, isError } = useModuleList();
  const updateModule = useUpdateModule();
  const reorderModules = useReorderModules();
  const queryClient = useQueryClient();

  const [editMode, setEditMode] = useState(false);
  const [editModules, setEditModules] = useState<ModuleConfig[]>([]);
  const [fieldPatches, setFieldPatches] = useState<Record<string, FieldPatch>>(
    {},
  );
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState("");
  const [iconPickerFor, setIconPickerFor] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [toast, setToast] = useState<
    { kind: "success" | "error"; message: string } | null
  >(null);

  function enterEditMode() {
    setEditModules(cloneModules(modules));
    setFieldPatches({});
    setEditMode(true);
  }

  function cancelEdit() {
    setEditModules([]);
    setFieldPatches({});
    setEditMode(false);
  }

  const activeList: ModuleConfig[] = editMode ? editModules : modules;

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return activeList;
    return activeList
      .map((m) => {
        const selfMatch = m.label.toLowerCase().includes(term);
        const matchingKids = m.children.filter((c) =>
          c.label.toLowerCase().includes(term),
        );
        if (selfMatch) return m;
        if (matchingKids.length > 0) return { ...m, children: matchingKids };
        return null;
      })
      .filter((m): m is ModuleConfig => m !== null);
  }, [activeList, search]);

  function patchField(id: string, patch: FieldPatch) {
    setFieldPatches((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    setEditModules((prev) =>
      prev.map((m) => {
        if (m.id === id) return { ...m, ...patch };
        return {
          ...m,
          children: m.children.map((c) =>
            c.id === id ? { ...c, ...patch } : c,
          ),
        };
      }),
    );
  }

  function moveParentUp(index: number) {
    if (index <= 0) return;
    setEditModules((prev) => {
      const list = [...prev];
      [list[index - 1], list[index]] = [list[index]!, list[index - 1]!];
      list.forEach((m, i) => {
        m.sortOrder = (i + 1) * 100;
        m.children = m.children.map((c, ci) => ({
          ...c,
          sortOrder: m.sortOrder + ci + 1,
        }));
      });
      return list;
    });
  }

  function moveParentDown(index: number) {
    setEditModules((prev) => {
      if (index < 0 || index >= prev.length - 1) return prev;
      const list = [...prev];
      [list[index + 1], list[index]] = [list[index]!, list[index + 1]!];
      list.forEach((m, i) => {
        m.sortOrder = (i + 1) * 100;
        m.children = m.children.map((c, ci) => ({
          ...c,
          sortOrder: m.sortOrder + ci + 1,
        }));
      });
      return list;
    });
  }

  function moveChildUp(parentId: string, childIndex: number) {
    setEditModules((prev) =>
      prev.map((m) => {
        if (m.id !== parentId) return m;
        if (childIndex <= 0) return m;
        const children = [...m.children];
        [children[childIndex - 1], children[childIndex]] = [
          children[childIndex]!,
          children[childIndex - 1]!,
        ];
        const renumbered = children.map((c, i) => ({
          ...c,
          sortOrder: m.sortOrder + i + 1,
        }));
        return { ...m, children: renumbered };
      }),
    );
  }

  function moveChildDown(parentId: string, childIndex: number) {
    setEditModules((prev) =>
      prev.map((m) => {
        if (m.id !== parentId) return m;
        if (childIndex < 0 || childIndex >= m.children.length - 1) return m;
        const children = [...m.children];
        [children[childIndex + 1], children[childIndex]] = [
          children[childIndex]!,
          children[childIndex + 1]!,
        ];
        const renumbered = children.map((c, i) => ({
          ...c,
          sortOrder: m.sortOrder + i + 1,
        }));
        return { ...m, children: renumbered };
      }),
    );
  }

  async function handleSave() {
    setSaving(true);
    setToast(null);
    try {
      const fieldUpdates: { id: string; payload: UpdateModulePayload }[] = [];
      for (const [id, patch] of Object.entries(fieldPatches)) {
        if (Object.keys(patch).length > 0) {
          fieldUpdates.push({ id, payload: patch });
        }
      }

      const reorderUpdates: { id: string; sortOrder: number }[] = [
        ...editModules.map((m) => ({ id: m.id, sortOrder: m.sortOrder })),
        ...editModules.flatMap((m) =>
          m.children.map((c) => ({ id: c.id, sortOrder: c.sortOrder })),
        ),
      ];

      const tasks: Promise<unknown>[] = [];
      for (const f of fieldUpdates) tasks.push(updateModule.mutateAsync(f));
      if (reorderUpdates.length > 0)
        tasks.push(reorderModules.mutateAsync(reorderUpdates));
      await Promise.all(tasks);

      await queryClient.invalidateQueries({ queryKey: ["modules"] });
      await queryClient.invalidateQueries({ queryKey: ["modules", "nav"] });
      const refetched = await queryClient.refetchQueries({
        queryKey: ["modules", "nav"],
        type: "active",
      });
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.debug("[ModuleManager] nav modules after refetch:", refetched);
      }

      setEditMode(false);
      setEditModules([]);
      setFieldPatches({});
      await new Promise((resolve) => window.setTimeout(resolve, 500));
      setToast({
        kind: "success",
        message: "Module order saved. Sidebar updated.",
      });
      window.setTimeout(() => setToast(null), 2500);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save changes";
      setToast({ kind: "error", message: msg });
      window.setTimeout(() => setToast(null), 4000);
    } finally {
      setSaving(false);
    }
  }

  type Line =
    | {
        kind: "parent";
        module: ModuleConfig;
        parentIndex: number;
        hasChildren: boolean;
        isCollapsed: boolean;
        isLocked: boolean;
        canUp: boolean;
        canDown: boolean;
      }
    | {
        kind: "child";
        module: ModuleConfig;
        parentId: string;
        childIndex: number;
        canUp: boolean;
        canDown: boolean;
      };

  const lines: Line[] = [];
  filtered.forEach((m, index) => {
    const isCollapsed = !!collapsed[m.id];
    const isLocked = m.name === ADMINISTRATION_NAME;
    lines.push({
      kind: "parent",
      module: m,
      parentIndex: index,
      hasChildren: m.children.length > 0,
      isCollapsed,
      isLocked,
      canUp: index > 0,
      canDown: index < filtered.length - 1,
    });
    if (!isCollapsed && m.children.length > 0) {
      m.children.forEach((c, childIndex) => {
        lines.push({
          kind: "child",
          module: c,
          parentId: m.id,
          childIndex,
          canUp: childIndex > 0,
          canDown: childIndex < m.children.length - 1,
        });
      });
    }
  });

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Administration" },
        { label: "Module Manager" },
      ]}
    >
      <div className="w-full space-y-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <LayoutDashboard
              className="h-[18px] w-[18px] text-foreground"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-semibold tracking-tight">
              Module Manager
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {editMode ? (
              <>
                <Button variant="outline" onClick={cancelEdit} disabled={saving}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving && (
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                  )}
                  Save Changes
                </Button>
              </>
            ) : (
              <Button onClick={enterEditMode}>
                <Pencil className="h-4 w-4" strokeWidth={1.5} />
                Edit
              </Button>
            )}
          </div>
        </div>

        {toast && (
          <div
            className={cn(
              "rounded-md border px-4 py-2 text-sm",
              toast.kind === "success"
                ? "border-[hsl(142,72%,45%)]/40 bg-[hsl(142,72%,96%)] text-[hsl(142,72%,28%)]"
                : "border-destructive/40 bg-destructive/5 text-destructive",
            )}
          >
            {toast.message}
          </div>
        )}

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            Failed to load modules.
          </div>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <div className="relative w-[280px]">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Input
                  placeholder="Search modules..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <div
              className="overflow-hidden rounded-xl border bg-card"
              style={{ borderColor: CARD_BORDER }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                  <Loader2
                    className="mr-2 h-4 w-4 animate-spin"
                    strokeWidth={1.5}
                  />
                  Loading modules…
                </div>
              ) : lines.length === 0 ? (
                <div className="py-16 text-center text-sm text-muted-foreground">
                  No modules match the current search.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-6 py-3">Module</th>
                      <th className="px-6 py-3">Status</th>
                      <th className="px-6 py-3">Sidebar</th>
                      <th className="px-6 py-3">Icon</th>
                      <th className="px-6 py-3">Order</th>
                      <th className="px-6 py-3">System</th>
                      <th className="px-6 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {lines.map((line) => {
                      const m = line.module;
                      const Icon = getIcon(m.icon);
                      const isParent = line.kind === "parent";
                      const isOpen = openMenuId === m.id;

                      return (
                        <tr
                          key={m.id}
                          className={cn(
                            "transition-colors",
                            isParent
                              ? "bg-[hsl(210,30%,97%)] hover:bg-[hsl(210,30%,95%)]"
                              : "hover:bg-[hsl(210,50%,98%)]",
                          )}
                        >
                          <td className="px-6 py-3">
                            <div
                              className="flex items-center gap-2"
                              style={{ paddingLeft: isParent ? 0 : 32 }}
                            >
                              {isParent ? (
                                line.hasChildren ? (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setCollapsed((prev) => ({
                                        ...prev,
                                        [m.id]: !prev[m.id],
                                      }))
                                    }
                                    className="flex h-5 w-5 items-center justify-center rounded hover:bg-[hsl(210,30%,92%)]"
                                    aria-label="Toggle children"
                                  >
                                    {line.isCollapsed ? (
                                      <ChevronRight
                                        className="h-3.5 w-3.5"
                                        strokeWidth={1.5}
                                      />
                                    ) : (
                                      <ChevronDown
                                        className="h-3.5 w-3.5"
                                        strokeWidth={1.5}
                                      />
                                    )}
                                  </button>
                                ) : (
                                  <span className="inline-block w-5" />
                                )
                              ) : (
                                <CornerDownRight
                                  className="h-3.5 w-3.5 text-muted-foreground"
                                  strokeWidth={1.5}
                                />
                              )}
                              <Icon
                                className="h-4 w-4 text-foreground"
                                strokeWidth={1.5}
                              />
                              <span
                                className={cn(
                                  "text-foreground",
                                  isParent ? "font-semibold" : "font-normal",
                                )}
                              >
                                {m.label}
                              </span>
                              {isParent && m.isParent && <ParentBadge />}
                            </div>
                          </td>
                          <td className="px-6 py-3">
                            <Toggle
                              checked={m.isEnabled}
                              disabled={!editMode || m.isSystem}
                              title={
                                m.isSystem
                                  ? "System modules cannot be disabled"
                                  : undefined
                              }
                              onChange={(v) =>
                                patchField(m.id, { isEnabled: v })
                              }
                            />
                          </td>
                          <td className="px-6 py-3">
                            <Toggle
                              checked={m.showInSidebar}
                              disabled={!editMode}
                              onChange={(v) =>
                                patchField(m.id, { showInSidebar: v })
                              }
                            />
                          </td>
                          <td className="px-6 py-3">
                            <button
                              type="button"
                              disabled={!editMode}
                              onClick={() =>
                                editMode && setIconPickerFor(m.id)
                              }
                              className={cn(
                                "flex items-center gap-2 rounded px-1 py-0.5",
                                editMode &&
                                  "cursor-pointer hover:bg-[hsl(210,50%,96%)]",
                              )}
                            >
                              <Icon
                                className="h-4 w-4 text-foreground"
                                strokeWidth={1.5}
                              />
                              <span className="font-mono text-[11px] text-muted-foreground">
                                {m.icon}
                              </span>
                            </button>
                          </td>
                          <td className="px-6 py-3">
                            {editMode ? (
                              isParent && line.isLocked ? (
                                <div
                                  className="flex h-6 w-6 items-center justify-center rounded border border-border text-muted-foreground"
                                  title="Administration is locked — cannot be reordered"
                                >
                                  <Lock
                                    className="h-3 w-3"
                                    strokeWidth={1.5}
                                  />
                                </div>
                              ) : (
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    disabled={!line.canUp}
                                    onClick={() => {
                                      if (isParent) {
                                        moveParentUp(line.parentIndex);
                                      } else {
                                        moveChildUp(
                                          line.parentId,
                                          line.childIndex,
                                        );
                                      }
                                    }}
                                    className={cn(
                                      "flex h-6 w-6 items-center justify-center rounded border",
                                      line.canUp
                                        ? "cursor-pointer border-border hover:bg-[hsl(210,50%,96%)]"
                                        : "border-border opacity-40",
                                    )}
                                  >
                                    <ArrowUp
                                      className="h-3 w-3"
                                      strokeWidth={1.5}
                                    />
                                  </button>
                                  <button
                                    type="button"
                                    disabled={!line.canDown}
                                    onClick={() => {
                                      if (isParent) {
                                        moveParentDown(line.parentIndex);
                                      } else {
                                        moveChildDown(
                                          line.parentId,
                                          line.childIndex,
                                        );
                                      }
                                    }}
                                    className={cn(
                                      "flex h-6 w-6 items-center justify-center rounded border",
                                      line.canDown
                                        ? "cursor-pointer border-border hover:bg-[hsl(210,50%,96%)]"
                                        : "border-border opacity-40",
                                    )}
                                  >
                                    <ArrowDown
                                      className="h-3 w-3"
                                      strokeWidth={1.5}
                                    />
                                  </button>
                                </div>
                              )
                            ) : (
                              <span className="font-mono text-[12px] text-muted-foreground">
                                {m.sortOrder}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-3">
                            {m.isSystem && <SystemBadge />}
                          </td>
                          <td className="px-6 py-3 text-right">
                            <div className="relative inline-block">
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Row actions"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenuId(isOpen ? null : m.id);
                                }}
                              >
                                <MoreHorizontal
                                  className="h-4 w-4"
                                  strokeWidth={1.5}
                                />
                              </Button>
                              {isOpen && (
                                <div
                                  className="absolute right-0 z-10 mt-1 w-28 rounded-md border bg-card shadow-md"
                                  style={{ borderColor: CARD_BORDER }}
                                >
                                  <button
                                    type="button"
                                    className="block w-full px-3 py-2 text-left text-sm hover:bg-[hsl(210,50%,96%)]"
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      if (!editMode) enterEditMode();
                                    }}
                                  >
                                    Edit
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>

      {iconPickerFor && (
        <IconPickerModal
          open={!!iconPickerFor}
          onOpenChange={(o) => {
            if (!o) setIconPickerFor(null);
          }}
          currentIcon={
            activeList
              .flatMap((m) => [m, ...m.children])
              .find((m) => m.id === iconPickerFor)?.icon ?? "LayoutDashboard"
          }
          onSelect={(icon) => {
            if (iconPickerFor) patchField(iconPickerFor, { icon });
          }}
        />
      )}
    </AppLayout>
  );
}
